<template>
  <div class="space-y-2 rounded-md bg-white p-2 pb-1 pt-1.5">
    <div v-if="transfer[`${mode}LocationType`] === BookingLocationType.STATION" class="space-y-2">
      <div class="font-medium">{{ t('station') }}</div>
      <div>
        {{ getLocalizedField(transfer[`${mode}ResponsibleStation`]?.info) }}<br />{{
          transfer[`${mode}ResponsibleStation`]?.street
        }}<br />{{
          `${transfer[`${mode}ResponsibleStation`]?.zip} ${transfer[`${mode}ResponsibleStation`]?.city}`
        }}
      </div>
    </div>
    <div v-else class="space-y-2">
      <div class="font-medium">{{ t('street') }}</div>
      <div>{{ transfer[`${mode}LocationGeocodedAddress`]?.displayName }}</div>
    </div>
    <a
      v-if="navigationUrl"
      :class="button({ variant: 'secondary', size: 'sm', className: 'w-full md:hidden' })"
      :href="navigationUrl"
      target="_blank"
    >
      <NavigationIcon class="h-4 w-4" />
      {{ t('navigate') }}
    </a>
  </div>
</template>

<script lang="ts" setup>
import { BookingLocationType } from '@/entities/bookings/booking-location-type.enum.ts';
import { useLocalizedFields } from '@/hooks/use-localized-fields.ts';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import { NavigationIcon } from 'lucide-vue-next';
import { button } from '../../../../theme/lib/cva/button';
import type { Allocation } from '@/entities/allocations/allocation.entity.ts';

const props = defineProps<{
  transfer: Allocation;
  mode: 'pickup' | 'dropoff';
}>();

const { t } = useI18n();
const { getLocalizedField } = useLocalizedFields();

const navigationUrl = computed(() => {
  let latitude: number | null = null;
  let longitude: number | null = null;
  let addressQuery: string | null = null;

  if (props.transfer[`${props.mode}LocationType`] === BookingLocationType.STATION) {
    const station = props.transfer[`${props.mode}ResponsibleStation`];
    if (station?.location?.coordinates) {
      longitude = station.location.coordinates[0];
      latitude = station.location.coordinates[1];
      addressQuery = encodeURIComponent(`${station.street} ${station.zip} ${station.city}`);
    }
  } else {
    const location = props.transfer[`${props.mode}Location`];
    if (location?.coordinates) {
      longitude = location.coordinates[0];
      latitude = location.coordinates[1];
    }
    const displayName = props.transfer[`${props.mode}LocationGeocodedAddress`]?.displayName;
    if (displayName) {
      addressQuery = encodeURIComponent(displayName);
    }
  }

  if (latitude && longitude) {
    // Use Apple Maps URL which works on iOS natively and redirects to Google Maps on Android
    return `https://maps.apple.com/?ll=${latitude},${longitude}${addressQuery ? `&q=${addressQuery}` : ''}`;
  }

  if (addressQuery) {
    // Fallback to query-based navigation if coordinates are not available
    return `https://maps.apple.com/?q=${addressQuery}`;
  }

  return null;
});
</script>

<i18n lang="json">
{
  "en": {
    "street": "Street",
    "station": "Station",
    "transferStart": "Transfer Start",
    "transferDestination": "Transfer Destination",
    "navigate": "Navigate"
  },
  "de": {
    "street": "Straße",
    "station": "Station",
    "transferStart": "Transferstartort",
    "transferDestination": "Transferziel",
    "navigate": "Navigieren"
  },
  "es": {
    "street": "Calle",
    "station": "Estación",
    "transferStart": "Inicio del traslado",
    "transferDestination": "Destino del traslado",
    "navigate": "Navegar"
  }
}
</i18n>
